import React, { Component } from 'react';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { compose, graphql } from 'react-apollo';
import Navbar from 'react-bootstrap/Navbar';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { SELECTION_EVENT, trackUserEvent } from 'userActivityMiddleware';
import { toggleCasinoSounds } from 'containers/App/actions';
import { makeSelectCasinoSoundsOn } from 'containers/App/selectors';
import { toggleFeedVisibility } from 'containers/Feed/actions';
import { selectShowFeed } from 'containers/Feed/selectors';
import HomeIcon from 'components/HomeIcon';
import { setTourActive } from 'components/IntroTour/actions';
import MainMenuDropdown from 'components/MainMenuDropdown';
import { setOnboardActive } from 'components/OnboardTour/actions';
import PropertyDropdown from 'components/PropertyDropdown';
import RewardsButton from 'components/RewardsButton';
import SkipNavButton from 'components/SkipNavButton';
import { VaultButton } from 'components/VaultButton';
import WStoreButton from 'components/WStoreButton';
import RootClasses from 'utils/RootClasses';
import {
  CV_WEB_NEW_ONBOARD_EXP_ENABLED,
  SM_BREAKPOINT_AND_DOWN
} from 'utils/constants';
import { getUserData } from 'utils/graphQLFunctions';
import { QUERY_GET_REMOTE_CONFIG_ENTRY } from 'utils/hook/query/useRemoteConfigEntry';
import { errorShape } from 'utils/shapes';
import withMediaQuery from 'utils/withMediaQuery';
import withSuppressAsSecondaryContent from 'utils/withSuppressAsSecondaryContent';
import HeaderProfileIcon from './HeaderProfileIcon';
import { MainMenuItems } from './MainMenuItems';
import { setMenuOpen } from './actions';
import { selectMenuOpen } from './selectors';
import './Header.css';

class Header extends Component {
  constructor(props) {
    super(props);
    this.ignoreNextToggle = false;
    this.ignoreCurrentToggle = false;
    this.currentPageUrl = null;
  }

  render() {
    const {
      userData,
      toggleCasinoSounds,
      casinoSoundsOn,
      startSiteTour,
      startOnboard,
      menuOpen,
      setMenuOpen,
      toggleFeedVisibility,
      newOnboardEnabledData
    } = this.props;

    const user = userData.user;

    const handleIgnoreNextToggle = () => {
      this.ignoreNextToggle = true;
    };

    const handleIgnoreCurrentToggle = () => {
      this.ignoreCurrentToggle = true;
    };

    const trackMenuToggleEvent = open => {
      trackUserEvent({
        activityType: SELECTION_EVENT,
        eventName: 'toggle-main-menu',
        eventValue: open ? 'open' : 'close',
        initiator: 'main-menu-dropdown',
        ...(!open && {
          referrerPage: this.props.pathname,
          page: this.currentPageUrl
        })
      });
    };

    const handleMainMenuToggle = (isOpen, navUrl) => {
      if (menuOpen === isOpen) {
        return;
      }

      if (this.ignoreCurrentToggle) {
        this.ignoreCurrentToggle = false;
        return;
      }

      setMenuOpen(isOpen);

      if (isOpen) {
        trackMenuToggleEvent(isOpen);
      } else {
        if (this.ignoreNextToggle) {
          this.ignoreNextToggle = false;
          this.currentPageUrl = navUrl;
        } else {
          trackMenuToggleEvent(isOpen);
          this.currentPageUrl = null;
        }
      }
    };

    const handleStartSiteTour = () => {
      if (!!newOnboardEnabledData?.remoteConfigEntry?.value) {
        startOnboard();
      } else {
        startSiteTour();
      }

      trackUserEvent({
        activityType: SELECTION_EVENT,
        eventName: 'toggle-site-tour',
        eventValue: 'start-site-tour',
        initiator: 'main-menu-dropdown'
      });
    };

    return (
      <div className="header">
        <RootClasses className="with-header" />
        <Navbar fixed="top" aria-label="Header">
          <HomeIcon user={user} onClick={() => toggleFeedVisibility(false)} />
          <PropertyDropdown />
          <SkipNavButton />
          <Link
            to={{
              pathname: '/vault',
              state: { initiator: 'header-vault-icon' }
            }}
          >
            <VaultButton className="d-min-md" />
          </Link>
          <div className="header-spacer" />
          <WStoreButton
            className="d-min-md"
            useIcon={true}
            to={{
              pathname: '/wstore',
              state: { initiator: 'header-wstore-icon' }
            }}
          />
          <div className="header-spacer" />
          <Link
            to={{
              pathname: '/rewards',
              state: { initiator: 'header-rewards-icon' }
            }}
          >
            <RewardsButton className="d-min-md" />
          </Link>
          <div className="header-spacer" />
          {user && (
            <>
              <Link
                to={{
                  pathname: '/profile',
                  state: { initiator: 'header-profile-icon' }
                }}
              >
                <HeaderProfileIcon user={user} />
              </Link>
              <div className="header-spacer" />
            </>
          )}
          <MainMenuDropdown
            show={menuOpen}
            withHover={false}
            onToggle={isOpen => handleMainMenuToggle(isOpen)}
          >
            <MainMenuItems
              user={user}
              startSiteTour={handleStartSiteTour}
              isMobileScreen={this.props.matchesBreakpoint}
              onMenuToggle={handleMainMenuToggle}
              onIgnoreNextToggle={handleIgnoreNextToggle}
              onIgnoreCurrentToggle={handleIgnoreCurrentToggle}
              toggleSocialFeed={toggleFeedVisibility}
              casinoSoundsOn={casinoSoundsOn}
              toggleCasinoSounds={toggleCasinoSounds}
            />
          </MainMenuDropdown>
        </Navbar>
      </div>
    );
  }
}

Header.propTypes = {
  data: PropTypes.shape({
    user: PropTypes.shape({
      email: PropTypes.string,
      avatarUrl: PropTypes.string,
      displayName: PropTypes.string
    }),
    loading: PropTypes.bool,
    error: errorShape
  }),
  menuOpen: PropTypes.bool,
  profileOpen: PropTypes.bool,
  casinoSoundsOn: PropTypes.bool,
  pathname: PropTypes.string
};

const mapDispatchToProps = dispatch => ({
  startSiteTour: () => dispatch(setTourActive(true)),
  startOnboard: () => dispatch(setOnboardActive(true)),
  setMenuOpen: open => dispatch(setMenuOpen(open)),
  toggleFeedVisibility: show => dispatch(toggleFeedVisibility(show)),
  toggleCasinoSounds: () => dispatch(toggleCasinoSounds())
});

const mapStateToProps = createStructuredSelector({
  menuOpen: selectMenuOpen(),
  showFeed: selectShowFeed(),
  casinoSoundsOn: makeSelectCasinoSoundsOn()
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

const REMOTE_CONFIG_EVENT = gql`
  subscription remoteConfigEvent {
    remoteConfigEvent {
      action
      entries {
        key
        value
      }
    }
  }
`;

export default compose(
  withMediaQuery(SM_BREAKPOINT_AND_DOWN),
  withSuppressAsSecondaryContent,
  withConnect,
  getUserData({
    name: 'userData'
  }),
  graphql(QUERY_GET_REMOTE_CONFIG_ENTRY, {
    name: 'newOnboardEnabledData',
    options: {
      variables: {
        key: CV_WEB_NEW_ONBOARD_EXP_ENABLED
      },
      fetchPolicy: 'cache-and-network'
    },
    props: ({ ownProps, newOnboardEnabledData }) => {
      return {
        newOnboardEnabledData,
        subscribeToNewData: () => {
          return newOnboardEnabledData.subscribeToMore({
            document: REMOTE_CONFIG_EVENT,
            updateQuery: (prev, { subscriptionData }) => {
              if (subscriptionData?.data?.action === 'Update') {
                subscriptionData?.data?.entries.forEach(({ key, value }) => {
                  if (key === CV_WEB_NEW_ONBOARD_EXP_ENABLED) {
                    prev.remoteConfigEntry.value = value;
                  }
                });
              }
              return prev;
            }
          });
        }
      };
    }
  })
)(Header);
